/* ─────────────────────────────────────────────────────────────
   Medicore cart drawer

   Line changes go through /cart/change.js, then the drawer re-renders
   itself from ?section_id=cart-drawer rather than patching the DOM by
   hand. That keeps money formatting, discounts and translations coming
   from Liquid, so the drawer can never disagree with the cart page.

   No jQuery. No dependencies.
   ───────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var SECTION_ID = 'cart-drawer';
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function root() {
    return document.getElementById('CartDrawer');
  }

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ── open / close ─────────────────────────────────────────── */

  var lastFocused = null;

  function open() {
    var el = root();
    if (!el || el.classList.contains('is-open')) return;

    lastFocused = document.activeElement;
    el.hidden = false;
    el.setAttribute('aria-hidden', 'false');

    // Force a reflow so the transform transition actually runs.
    void el.offsetWidth;
    el.classList.add('is-open');

    document.documentElement.classList.add('cart-drawer-open');
    document.body.style.overflow = 'hidden';

    var target = el.querySelector('.cart-drawer__close');
    if (target) target.focus();
  }

  function close() {
    var el = root();
    if (!el || !el.classList.contains('is-open')) return;

    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('cart-drawer-open');
    document.body.style.overflow = '';

    var done = function () {
      if (!el.classList.contains('is-open')) el.hidden = true;
    };
    var panel = el.querySelector('.cart-drawer__panel');
    if (panel) {
      panel.addEventListener('transitionend', done, { once: true });
      setTimeout(done, 400); // fallback if the transition never fires
    } else {
      done();
    }

    if (lastFocused && document.contains(lastFocused)) lastFocused.focus();
    lastFocused = null;
  }

  function trapFocus(event) {
    var el = root();
    if (!el || !el.classList.contains('is-open') || event.key !== 'Tab') return;

    var panel = el.querySelector('.cart-drawer__panel');
    if (!panel) return;

    var items = Array.prototype.filter.call(
      panel.querySelectorAll(FOCUSABLE),
      function (n) { return n.offsetParent !== null; }
    );
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ── rendering ────────────────────────────────────────────── */

  function setBusy(state) {
    var el = root();
    if (el) el.classList.toggle('is-busy', !!state);
  }

  function syncHeaderCount(count) {
    document.querySelectorAll('.cart-count, .btn_cart_count').forEach(function (node) {
      // The popup's counter is rendered as "( n )"; the header's is bare.
      node.textContent = node.classList.contains('btn_cart_count') ? '( ' + count + ' )' : String(count);
    });
  }

  function render() {
    return fetch(routeRoot() + '?section_id=' + SECTION_ID, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Cart drawer section ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var el = root();
        if (!el) return;

        var fresh = new DOMParser()
          .parseFromString(html, 'text/html')
          .getElementById('CartDrawer');
        if (!fresh) return;

        var wasOpen = el.classList.contains('is-open');
        var panel = el.querySelector('.cart-drawer__panel');
        var freshPanel = fresh.querySelector('.cart-drawer__panel');
        if (panel && freshPanel) {
          panel.innerHTML = freshPanel.innerHTML;
        }

        el.dataset.itemCount = fresh.dataset.itemCount || '0';
        syncHeaderCount(el.dataset.itemCount);

        if (wasOpen) el.classList.add('is-open');
      });
  }

  /* ── line mutations ───────────────────────────────────────── */

  function showLineError(line, message) {
    var node = line && line.querySelector('[data-line-error]');
    if (!node) return;
    node.textContent = message;
    node.hidden = false;
  }

  function changeLine(lineEl, quantity) {
    if (!lineEl) return;

    var line = lineEl.dataset.line;
    var errorNode = lineEl.querySelector('[data-line-error]');
    if (errorNode) errorNode.hidden = true;

    lineEl.classList.add('is-busy');
    setBusy(true);

    fetch(routeRoot() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: quantity })
    })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw new Error(data.description || data.message || 'Could not update the cart.');
          return data;
        });
      })
      .then(function (cart) {
        // Shopify silently clamps to available stock rather than erroring.
        var updated = cart.items && cart.items[Number(line) - 1];
        if (quantity > 0 && updated && updated.quantity < quantity) {
          showLineError(lineEl, 'Only ' + updated.quantity + ' available.');
        }
        return render();
      })
      .catch(function (error) {
        showLineError(lineEl, error.message);
        lineEl.classList.remove('is-busy');
      })
      .finally(function () {
        setBusy(false);
      });
  }

  /* ── events ───────────────────────────────────────────────── */

  document.addEventListener('click', function (event) {
    // open triggers: the header cart link, or anything opted in
    var opener = event.target.closest('.header-cart, [data-cart-drawer-open]');
    if (opener) {
      event.preventDefault();
      open();
      return;
    }

    if (event.target.closest('[data-cart-drawer-close]')) {
      event.preventDefault();
      close();
      return;
    }

    var line = event.target.closest('.cart-item');
    if (!line) return;

    var input = line.querySelector('[data-quantity-input]');

    if (event.target.closest('[data-cart-remove]')) {
      event.preventDefault();
      changeLine(line, 0);
      return;
    }
    if (event.target.closest('[data-quantity-decrease]')) {
      event.preventDefault();
      changeLine(line, Math.max(0, (parseInt(input.value, 10) || 0) - 1));
      return;
    }
    if (event.target.closest('[data-quantity-increase]')) {
      event.preventDefault();
      changeLine(line, (parseInt(input.value, 10) || 0) + 1);
    }
  });

  document.addEventListener('change', function (event) {
    var input = event.target.closest('[data-quantity-input]');
    if (!input) return;
    var line = input.closest('.cart-item');
    var value = parseInt(input.value, 10);
    if (isNaN(value) || value < 0) value = 0;
    changeLine(line, value);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') close();
    trapFocus(event);
  });

  // Opened by the product form after a successful add.
  document.addEventListener('cart:added', function () {
    render().then(open);
  });
  document.addEventListener('cart:refresh', function () {
    render();
  });

  window.CartDrawer = { open: open, close: close, render: render };
})();
